<!--
	SafeCurves AI assistant transcript and composer; AppView owns the slide-out container and
	`dataChatStore` owns the state.
-->
<script lang="ts">
	import { Marked } from 'marked';
	import { prefersReducedMotion } from 'svelte/motion';
	import DOMPurify from 'dompurify';
	import { apiKeyStore } from '#lib/stores/apiKeyStore.svelte.js';
	import { agentFilterStore } from '#lib/stores/agentFilterStore.svelte.js';
	import { aiMapChatEnabled } from '#lib/config.js';
	import { getLogger } from '@logtape/logtape';
	import { CHAT_MODES, MAX_PROMPT_LENGTH, dataChatStore } from './dataChatStore.svelte';
	import { focusFeature } from './uiActions';
	import { tablistKeys } from '#lib/tablist.js';
	import IconButton from '#lib/components/IconButton.svelte';
	import AbleyButton from '#lib/components/AbleyButton.svelte';
	import LoadingSpinner from '#lib/components/LoadingSpinner.svelte';
	import UpArrowIcon from '#lib/assets/ai/up-arrow.svg?raw';
	import NewChatIcon from '#lib/assets/ai/new-chat.svg?raw';
	import BetaIcon from '#lib/assets/ai/beta.svg?raw';
	import XMarkIcon from '#lib/assets/x-mark.svg?raw';
	import StopIcon from '#lib/assets/stop.svg?raw';
	import MapLayersIcon from '#lib/assets/ai/map-layers.svg?raw';
	import DocumentsIcon from '#lib/assets/ai/documents.svg?raw';

	const log = getLogger(['safesystem', 'safecurves', 'chat-panel']);

	// Map & Data mode is only offered when its agent is enabled; otherwise doc chat only.
	const availableModes = aiMapChatEnabled ? CHAT_MODES : CHAT_MODES.filter((m) => m.id === 'docs');

	// Own instance: `marked.setOptions` would reconfigure the shared global.
	const md = new Marked({ breaks: true, gfm: true });
	const transcriptId = $props.id();

	const FEATURE_HREF_PREFIX = '#feature-';

	// Opening screen. Examples are real questions against this data, so a
	// curious first click returns something rather than an apology.
	const MODE_CARDS = [
		{
			id: 'data' as const,
			icon: MapLayersIcon,
			title: 'Map & data',
			meta: 'curves · corridors · crashes',
			body: "Ask about what's on the map. Filter to what matters, compare layers, and I'll put the answer on screen.",
			examples: [
				'Which corridors are highest priority in Pawnee County?',
				'Show crashes within 100m of Class 1 curves'
			]
		},
		{
			id: 'docs' as const,
			icon: DocumentsIcon,
			title: 'Project documents',
			meta: 'playbook · methodology · white papers',
			body: 'Ask questions in plain language across the SafeCurves documentation. Answers cite their source.',
			examples: ['How is the priority rating calculated?', 'When is an advisory sign recommended?']
		}
	];

	function renderAiText(text: string): string {
		const html = md.parse(text, { async: false }) as string;
		return DOMPurify.sanitize(html, {
			ADD_ATTR: ['target', 'rel']
		});
	}

	function handleSubmit() {
		const apiKey = apiKeyStore.apiKey;
		if (!apiKey) return;
		void dataChatStore.sendPrompt(apiKey);
	}

	function handleNewChat() {
		const apiKey = apiKeyStore.apiKey;
		if (!apiKey) return;
		void dataChatStore.startNewChat(apiKey);
	}

	function handleKeydown(e: KeyboardEvent) {
		// keyCode 229 covers Safari firing keydown after compositionend.
		if (e.isComposing || e.keyCode === 229) return;
		if (e.key === 'Enter' && !e.shiftKey && dataChatStore.canSubmit) {
			e.preventDefault();
			handleSubmit();
		}
	}

	// Delegated so agent links like `[Corridor 4821](#feature-4821)` in re-rendered markdown fly the map to that feature.
	function onTranscriptClick(event: MouseEvent) {
		const anchor = (event.target as HTMLElement).closest(`a[href^="${FEATURE_HREF_PREFIX}"]`);
		if (!anchor) return;
		event.preventDefault();
		const featureId = anchor.getAttribute('href')!.slice(FEATURE_HREF_PREFIX.length);
		if (!focusFeature(featureId)) {
			log.warn('no location known for feature {featureId}', { featureId });
		}
	}

	// Auto-scroll the latest entry into view when the transcript grows.
	let bottomAnchor = $state<HTMLDivElement | null>(null);
	$effect(() => {
		void dataChatStore.entries.length;
		void dataChatStore.isLoading;
		bottomAnchor?.scrollIntoView({
			behavior: prefersReducedMotion.current ? 'auto' : 'smooth',
			block: 'end'
		});
	});
</script>

<div class="dataChat">
	<div class="header">
		<div class="identity">
			<h2 class="title">SafeCurves assistant</h2>
			<span class="beta" role="img" aria-label="Beta">{@html BetaIcon}</span>
		</div>
		<div class="headerActions">
			<AbleyButton
				variant="text"
				size="sm"
				onclick={handleNewChat}
				aria-label="New chat"
				title="New chat">
				<span class="headerIcon">{@html NewChatIcon}</span>
			</AbleyButton>
			<AbleyButton
				variant="text"
				size="sm"
				onclick={() => dataChatStore.close()}
				aria-label="Close SafeCurves assistant"
				title="Close SafeCurves assistant">
				<span class="headerIcon">{@html XMarkIcon}</span>
			</AbleyButton>
		</div>
	</div>

	{#if dataChatStore.showModeChooser}
		<div class="modeChooser">
			<p class="chooserPrompt">What do you want to work with?</p>
			{#each MODE_CARDS as card (card.id)}
				<section class="modeCard">
					<button
						type="button"
						class="modeCardMain"
						onclick={() => dataChatStore.chooseMode(card.id)}>
						<span class="modeCardHead">
							<span class="modeCardIcon">{@html card.icon}</span>
							<span class="modeCardTitles">
								<span class="modeCardTitle">{card.title}</span>
								<span class="modeCardMeta">{card.meta}</span>
							</span>
						</span>
						<span class="modeCardBody">{card.body}</span>
					</button>
					<!-- Seed the composer rather than sending: an agent turn costs money
					     and the wording is usually worth adjusting first. -->
					{#each card.examples as example (example)}
						<button
							type="button"
							class="examplePrompt"
							onclick={() => dataChatStore.chooseMode(card.id, example)}>
							“{example}”
						</button>
					{/each}
				</section>
			{/each}
		</div>
	{:else}
		<!-- Each mode keeps its own transcript; hidden when only one mode is available. -->
		{#if availableModes.length > 1}
			<div class="modeTabs" role="tablist" aria-label="Assistant mode" {@attach tablistKeys}>
				{#each availableModes as tab (tab.id)}
					<button
						type="button"
						role="tab"
						class="modeTab"
						class:active={dataChatStore.mode === tab.id}
						aria-selected={dataChatStore.mode === tab.id}
						aria-controls={transcriptId}
						tabindex={dataChatStore.mode === tab.id ? 0 : -1}
						onclick={() => dataChatStore.setMode(tab.id)}>
						{tab.label}
						{#if dataChatStore.loadingMode === tab.id}
							<span class="tabBusy" aria-hidden="true"></span>
						{/if}
					</button>
				{/each}
			</div>
		{/if}

		<!-- Clicks come from links inside, which are keyboard-activatable themselves. -->
		<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions -->
		<div
			class="transcript"
			id={transcriptId}
			onclick={onTranscriptClick}
			role="log"
			aria-live="polite"
			aria-label="Assistant transcript">
			{#if dataChatStore.entries.length === 0}
				<div class="entry ai">
					{#if dataChatStore.mode === 'data'}
						<p>
							You're in AI display mode — I control what the map shows, starting from the view you
							had. Ask me to narrow, add, or remove things: "show me only the high priority
							corridors", then "add the fatal crashes near them". Close this panel to return to
							manual control.
						</p>
					{:else}
						<p>
							Ask me about the SafeCurves project documents — the Playbook, the methodology white
							papers and the primers. For example: "how are curves classified as out of context?" or
							"what does the Prioritize crash risk model account for?" I answer from the documents
							and don't change the map.
						</p>
					{/if}
				</div>
			{:else}
				{#each dataChatStore.entries as entry (entry.id)}
					{#if entry.role === 'human'}
						<div class="entry human">
							<p>{entry.text}</p>
						</div>
					{:else if entry.role === 'status'}
						<div class="statusRow">{entry.text}</div>
					{:else if entry.role === 'error'}
						<div class="entry errorEntry">
							<p>{entry.text}</p>
						</div>
					{:else}
						<div class="entry ai">
							<div class="aiBody">{@html renderAiText(entry.text)}</div>
						</div>
					{/if}
				{/each}
			{/if}

			{#if dataChatStore.loadingMode === dataChatStore.mode}
				<div class="statusRow live">
					<LoadingSpinner size="sm" decorative />
					<span>
						{dataChatStore.liveStatus ??
							(dataChatStore.mode === 'data' ? 'querying your data…' : 'searching the documents…')}
					</span>
				</div>
			{/if}

			<div bind:this={bottomAnchor}></div>
		</div>

		{#if dataChatStore.mode === 'data'}
			<div class="filterChip">
				<span class="chipText">
					{#if agentFilterStore.active}
						Showing: {agentFilterStore.labels.join('; ') || 'query results'}
					{:else}
						AI display mode — map is empty; ask me to show something
					{/if}
				</span>
				{#if agentFilterStore.active}
					<IconButton
						variant="quiet"
						size="xs"
						onclick={() => agentFilterStore.clearAll()}
						aria-label="Clear the canvas"
						title="Clear the canvas">
						{@html XMarkIcon}
					</IconButton>
				{/if}
			</div>
		{/if}

		<div class="inputRow">
			<textarea
				class="promptInput"
				aria-label={dataChatStore.mode === 'data'
					? 'Ask about your data'
					: 'Ask about the project documents'}
				placeholder={dataChatStore.mode === 'data'
					? 'Ask about your data...'
					: 'Ask about the project documents...'}
				value={dataChatStore.currentPrompt}
				maxlength={MAX_PROMPT_LENGTH}
				oninput={(e) => dataChatStore.setPrompt(e.currentTarget.value)}
				onkeydown={handleKeydown}></textarea>
			{#if dataChatStore.isLoading}
				<IconButton
					variant="solid"
					onclick={() => dataChatStore.cancel()}
					aria-label="Stop the assistant"
					title="Stop the assistant">
					{@html StopIcon}
				</IconButton>
			{:else}
				<IconButton
					variant="solid"
					disabled={!dataChatStore.canSubmit}
					onclick={handleSubmit}
					aria-label="Send message"
					title="Send message">
					{@html UpArrowIcon}
				</IconButton>
			{/if}
		</div>
	{/if}
</div>

<style>
	.dataChat {
		display: flex;
		flex-direction: column;
		padding: var(--space-4);
		height: 100%;
		color: var(--text-color);
	}

	.header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: var(--space-3);

		.identity {
			display: flex;
			align-items: center;
			gap: var(--space-3);
		}

		.title {
			font-weight: var(--weight-bold);
			font-size: var(--font-size-base);
		}

		.beta {
			display: inline-flex;
			align-items: center;
		}
	}

	.modeTabs {
		display: flex;
		gap: var(--space-1);
		margin-bottom: var(--space-3);
		border-bottom: 1px solid var(--border-color);

		.modeTab {
			display: flex;
			align-items: center;
			gap: var(--space-2);
			cursor: pointer;
			margin-bottom: -1px;
			border: none;
			border-bottom: 3px solid transparent;
			background: none;
			padding: var(--space-2) var(--space-3);
			color: var(--text-muted);
			font-size: var(--font-size-sm);

			&:hover {
				color: var(--text-color);
			}

			&.active {
				border-bottom-color: var(--accent-text);
				color: var(--text-color);
				font-weight: var(--weight-semibold);
			}

			&:focus-visible {
				outline-offset: -2px;
			}
		}

		.tabBusy {
			animation: tabBusyPulse 1.2s ease-in-out infinite;
			border-radius: var(--radius-full);
			background-color: var(--accent);
			width: var(--space-2);
			height: var(--space-2);

			@media (prefers-reduced-motion: reduce) {
				animation: none;
				opacity: 0.6;
			}
		}
	}

	@keyframes tabBusyPulse {
		0%,
		100% {
			opacity: 0.25;
		}
		50% {
			opacity: 1;
		}
	}

	.headerActions {
		display: flex;
		align-items: center;
		gap: var(--space-1);
	}

	.headerIcon {
		display: inline-flex;
		width: var(--target-min);
		height: var(--target-min);
	}

	.transcript {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: var(--space-3);
		padding: 0 var(--space-1);
		min-height: 0;
		overflow-y: auto;
	}

	.entry {
		border-radius: var(--radius);
		padding: var(--space-3) var(--space-4);

		p {
			text-align: left;
		}

		&.ai {
			margin-right: 4%;
			background-color: var(--surface-sunken);

			.aiBody :global {
				p {
					margin: 0 0 var(--space-2);
				}

				a {
					color: var(--link-color);
					text-decoration: underline;
				}

				/* Feature references are click-to-fly, not navigation. */
				a[href^='#feature-'] {
					cursor: pointer;
					text-decoration-style: dotted;
					text-underline-offset: 2px;
				}

				ul,
				ol {
					margin: 0 0 var(--space-2) var(--space-5);
				}

				/* Fixed layout shares the ~420px panel width between columns so tables wrap instead of scrolling. */
				table {
					display: table;
					margin: 0 0 var(--space-2);
					border-collapse: collapse;
					width: 100%;
					table-layout: fixed;
					font-size: var(--font-size-xs);
					line-height: 1.35;
				}

				th,
				td {
					vertical-align: top;
					border: 1px solid var(--border-color);
					padding: var(--space-1) var(--space-2);
					overflow-wrap: anywhere;
					text-align: left;
				}

				th {
					background-color: var(--surface-alt);
					font-weight: var(--weight-semibold);
				}

				pre {
					overflow-wrap: anywhere;
					white-space: pre-wrap;
				}

				code {
					overflow-wrap: anywhere;
				}
			}
		}

		&.human {
			margin-left: 10%;
			background-color: var(--accent);
			color: var(--accent-contrast);
		}

		&.errorEntry {
			margin-right: 10%;
			border: 1px solid var(--status-danger);
			color: var(--status-danger);
		}
	}

	.modeChooser {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: var(--space-4);
		padding-top: var(--space-2);
		overflow-y: auto;
	}

	.chooserPrompt {
		font-weight: var(--weight-semibold);
	}

	.modeCard {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		border: 1px solid var(--border-color);
		border-radius: var(--radius);
		background-color: var(--surface-raised);
		padding: var(--space-3);
	}

	.modeCardMain {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		cursor: pointer;
		border: none;
		border-radius: var(--radius);
		background: none;
		padding: var(--space-1);
		color: var(--text-color);
		text-align: left;

		&:hover .modeCardTitle {
			color: var(--accent-text);
		}

		&:focus-visible {
			outline-offset: -2px;
		}
	}

	.modeCardHead {
		display: flex;
		align-items: center;
		gap: var(--space-2);
	}

	.modeCardIcon {
		display: inline-flex;
		flex: none;
		justify-content: center;
		align-items: center;
		border: 1px solid var(--border-color);
		border-radius: var(--radius);
		width: 2.25rem;
		height: 2.25rem;
		color: var(--accent-text);

		& > :global(svg) {
			width: 1.25rem;
			height: 1.25rem;
		}
	}

	.modeCardTitles {
		display: flex;
		flex-direction: column;
	}

	.modeCardTitle {
		font-weight: var(--weight-semibold);
	}

	.modeCardMeta {
		color: var(--text-muted);
		font-size: var(--font-size-xs);
		font-family: var(--font-mono);
	}

	.modeCardBody {
		font-size: var(--font-size-sm);
		line-height: 1.4;
	}

	.examplePrompt {
		cursor: pointer;
		border: 1px dashed var(--border-strong);
		border-radius: var(--radius);
		background: none;
		padding: var(--space-2) var(--space-3);
		width: 100%;
		color: var(--text-muted);
		font-size: var(--font-size-sm);
		text-align: left;

		&:hover {
			border-style: solid;
			color: var(--text-color);
		}

		&:focus-visible {
			outline-offset: -2px;
		}
	}

	.statusRow {
		padding: 0 var(--space-2);
		color: var(--text-muted);
		font-size: var(--font-size-sm);
		font-family: var(--font-mono);
		overflow-wrap: anywhere;

		&.live {
			display: flex;
			align-items: center;
			gap: var(--space-2);
		}
	}

	.filterChip {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		margin-top: var(--space-2);
		border: 1px solid var(--border-color);
		border-radius: var(--radius);
		background-color: var(--surface-alt);
		padding: var(--space-1) var(--space-1) var(--space-1) var(--space-3);
		min-height: calc(var(--target-min) + var(--space-2));
		font-size: var(--font-size-sm);

		.chipText {
			flex: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	.inputRow {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		padding-top: var(--space-3);
	}

	.promptInput {
		flex: 1;
		box-shadow: var(--shadow-sm);
		padding: var(--space-3) var(--space-4);
		min-height: 3rem;
		max-block-size: 10lh;
		field-sizing: content;
		resize: none;
	}
</style>
